// ═══════════════════════════════════════════════════════════════════════════
// ForgotPassword.js  (frontend)
// ═══════════════════════════════════════════════════════════════════════════
// The frontend was fine — the issue was the backend /auth/forgot-password
// route. See the backend fix in auth_backend_fix.js in this same output.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
.fp-root{min-height:100dvh;display:flex;align-items:center;justify-content:center;
  background:#f3f4f6;font-family:'Inter',system-ui,sans-serif;padding:20px;
  -webkit-font-smoothing:antialiased}
.fp-card{width:100%;max-width:400px;background:#fff;border:1px solid #e5e7eb;
  border-radius:16px;padding:36px 32px;box-shadow:0 4px 24px rgba(0,0,0,0.06)}
@media(max-width:440px){.fp-card{padding:24px 18px}}
.fp-logo{font-family:'Clash Display',sans-serif;font-size:1.4rem;font-weight:700;
  color:#1c73ba;text-decoration:none;display:block;text-align:center;margin-bottom:28px}
.fp-logo span{color:#f59e0b}
.fp-icon{width:56px;height:56px;border-radius:14px;background:#e8f3fb;
  display:flex;align-items:center;justify-content:center;margin:0 auto 18px}
.fp-title{font-family:'Clash Display',sans-serif;font-size:1.35rem;font-weight:700;
  color:#111827;margin-bottom:6px;text-align:center}
.fp-sub{font-size:13px;color:#6b7280;text-align:center;margin-bottom:24px;line-height:1.65}
.fp-alert{padding:11px 14px;border-radius:9px;font-size:13px;margin-bottom:18px;
  display:flex;gap:9px;align-items:flex-start;line-height:1.5}
.fp-alert.error{background:#fef2f2;border:1px solid rgba(239,68,68,0.25);color:#b91c1c}
.fp-alert.success{background:#ecfdf5;border:1px solid rgba(16,185,129,0.25);color:#065f46}
.fp-field{margin-bottom:16px}
.fp-label{display:block;font-size:11px;font-weight:700;color:#6b7280;
  text-transform:uppercase;letter-spacing:1px;margin-bottom:5px}
.fp-input{width:100%;padding:10px 13px;background:#fff;border:1.5px solid #e5e7eb;
  border-radius:9px;color:#111827;font-family:'Inter',sans-serif;font-size:14px;
  outline:none;transition:border .15s}
.fp-input:focus{border-color:#1c73ba;box-shadow:0 0 0 3px rgba(28,115,186,0.1)}
.fp-btn{width:100%;padding:12px;border-radius:9px;border:none;
  background:#1c73ba;color:#fff;font-family:'Inter',sans-serif;font-size:14px;
  font-weight:700;cursor:pointer;transition:all .18s;display:flex;align-items:center;justify-content:center;gap:8px}
.fp-btn:hover:not(:disabled){background:#155fa0;transform:translateY(-1px)}
.fp-btn:disabled{opacity:0.6;cursor:not-allowed;transform:none}
.fp-back{display:flex;align-items:center;justify-content:center;gap:5px;margin-top:18px;
  font-size:13px;color:#6b7280;text-decoration:none;transition:color .15s}
.fp-back:hover{color:#1c73ba}
.fp-spinner{width:15px;height:15px;border:2px solid rgba(255,255,255,.3);border-top-color:#fff;
  border-radius:50%;animation:spin .7s linear infinite;display:inline-block;flex-shrink:0}
@keyframes spin{to{transform:rotate(360deg)}}
.fp-success-icon{width:72px;height:72px;border-radius:50%;background:#ecfdf5;
  border:2px solid rgba(16,185,129,0.3);display:flex;align-items:center;justify-content:center;
  margin:0 auto 20px}
.fp-steps{background:#f9fafb;border:1px solid #e5e7eb;border-radius:9px;padding:14px;margin-top:18px}
.fp-step{display:flex;align-items:flex-start;gap:10px;margin-bottom:10px;font-size:13px;color:#374151;line-height:1.5}
.fp-step:last-child{margin-bottom:0}
.fp-step-num{width:20px;height:20px;border-radius:50%;background:#1c73ba;color:#fff;font-size:11px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px}
.fp-resend{text-align:center;margin-top:16px;font-size:13px;color:#6b7280}
.fp-resend button{border:none;background:none;color:#1c73ba;font-size:13px;font-weight:600;cursor:pointer;font-family:'Inter',sans-serif;padding:0;text-decoration:underline}
`;

const LockIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#1c73ba" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);
const MailIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);
const BackArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

export default function ForgotPassword() {
  const [email,   setEmail]   = useState('');
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [resent,  setResent]  = useState(false);
  const [cooldown,setCooldown]= useState(0);

  const submit = async (e) => {
    e?.preventDefault();
    setError('');
    if (!email.trim()) { setError('Please enter your email address.'); return; }
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || '';
      if (err.response?.status === 404 || msg.toLowerCase().includes('not found')) {
        // Don't reveal whether email exists — show success anyway (security)
        setSent(true);
      } else {
        setError(msg || 'Something went wrong. Please try again.');
      }
    } finally { setLoading(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setResent(true);
      setTimeout(() => setResent(false), 3000);
      // 60s cooldown to prevent spam
      setCooldown(60);
      const t = setInterval(() => setCooldown(c => { if(c<=1){clearInterval(t);return 0;} return c-1; }), 1000);
    } catch {}
    setLoading(false);
  };

  return (
    <div className="fp-root">
      <style>{CSS}</style>
      <div className="fp-card">
        <a href="/" className="fp-logo">flap<span>.</span></a>

        {sent ? (
          // ── Success state ──────────────────────────────────────────
          <>
            <div className="fp-success-icon"><MailIcon/></div>
            <h1 className="fp-title">Check your inbox</h1>
            <p className="fp-sub">
              We sent a password reset link to<br/>
              <strong style={{ color: '#1c73ba' }}>{email}</strong>
            </p>

            <div className="fp-steps">
              {[
                'Open the email from Flap (check spam if not in inbox)',
                'Click the "Reset Password" button in the email',
                'Choose a new password — link expires in 1 hour',
              ].map((s, i) => (
                <div key={i} className="fp-step">
                  <div className="fp-step-num">{i+1}</div>
                  <span>{s}</span>
                </div>
              ))}
            </div>

            {resent && (
              <div className="fp-alert success" style={{ marginTop: 14, marginBottom: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Email resent!
              </div>
            )}

            <div className="fp-resend" style={{ marginTop: 16 }}>
              Didn't receive it?{' '}
              <button onClick={resend} disabled={cooldown > 0 || loading}>
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend email'}
              </button>
            </div>

            <Link to="/login" className="fp-back" style={{ marginTop: 20 }}>
              <BackArrow/> Back to login
            </Link>
          </>
        ) : (
          // ── Request form ───────────────────────────────────────────
          <>
            <div className="fp-icon"><LockIcon/></div>
            <h1 className="fp-title">Forgot password?</h1>
            <p className="fp-sub">
              Enter your email and we'll send you a link to reset your password.
            </p>

            {error && (
              <div className="fp-alert error">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <form onSubmit={submit}>
              <div className="fp-field">
                <label className="fp-label">Email address</label>
                <input
                  className="fp-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              <button type="submit" className="fp-btn" disabled={loading}>
                {loading ? <><span className="fp-spinner"/> Sending…</> : 'Send reset link'}
              </button>
            </form>

            <Link to="/login" className="fp-back">
              <BackArrow/> Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}